import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

/**
 * PÁGINA LANDING SEO OPTIMIZADA PARA PRONÓSTICOS DEPORTIVOS COLOMBIA 2025
 * Esta página está específicamente diseñada para ranking SEO en búsquedas de:
 * - "pronósticos deportivos Colombia"
 * - "predicciones fútbol Colombia"
 * - "apuestas deportivas Colombia"
 * - "BetPlay pronósticos"
 * - Y keywords relacionadas
 */

const PronosticosColombiaLanding = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top al cargar
    window.scrollTo(0, 0);
  }, []);

  const featuredPicks = [
    {
      title: "Partido del Día VIP",
      sport: "Fútbol",
      league: "Liga Colombiana BetPlay",
      confidence: "98%",
      type: "Exclusivo Premium"
    },
    {
      title: "Champions League",
      sport: "Fútbol",
      league: "UEFA Champions League",
      confidence: "95%",
      type: "Análisis IA"
    },
    {
      title: "Premier League",
      sport: "Fútbol",
      league: "Premier League",
      confidence: "95%",
      type: "Estadísticas Avanzadas"
    },
    {
      title: "NBA Playoffs",
      sport: "Baloncesto",
      league: "NBA",
      confidence: "93%",
      type: "Machine Learning"
    }
  ];

  const casasApuestas = [
    "BetPlay", "CodeRed", "Rushbet", "Wplay", "Betsson", 
    "bet365", "1xBet", "Rivalo", "Betway", "William Hill"
  ];

  return (
    <>
      <Helmet>
        {/* SEO TITLE */}
        <title>🏆 Pronósticos Deportivos Colombia 2025 | DeportivosPro #1 | 95% Precisión</title>
        
        {/* SEO DESCRIPTION */}
        <meta 
          name="description" 
          content="⚽ LOS MEJORES pronósticos deportivos de Colombia 2025 | 95% de efectividad COMPROBADA | Predicciones GANADORAS para BetPlay, CodeRed, Rushbet | IA avanzada | Liga Colombiana, Champions, Premier League, NBA | +2M usuarios ganando | ¡Pronósticos GRATIS hoy! 💰🚀" 
        />
        
        {/* KEYWORDS */}
        <meta 
          name="keywords" 
          content="pronósticos deportivos Colombia 2025, predicciones fútbol Colombia, apuestas deportivas Colombia, BetPlay pronósticos, CodeRed tips, Rushbet análisis, Wplay picks, pronósticos Liga Colombiana, predicciones Champions League Colombia, picks deportivos Colombia, DeportivosPro Colombia, pronósticos gratis Colombia, apuestas seguras Colombia, tips deportivos Colombia 2025" 
        />

        {/* CANONICAL */}
        <link rel="canonical" href="https://deportivospro.com/pronosticos-colombia" />

        {/* OPEN GRAPH */}
        <meta property="og:title" content="🏆 Pronósticos Deportivos #1 en Colombia 2025 | DeportivosPro" />
        <meta property="og:description" content="Los pronósticos deportivos MÁS PRECISOS de Colombia | 95% efectividad | BetPlay, CodeRed, Rushbet | +2M usuarios ganando" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://deportivospro.com/pronosticos-colombia" />
        
        {/* TWITTER CARD */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="🏆 Pronósticos Deportivos #1 Colombia 2025" />
        <meta name="twitter:description" content="95% de efectividad COMPROBADA | Pronósticos GANADORES | BetPlay, CodeRed, Rushbet" />

        {/* STRUCTURED DATA - BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Inicio",
                "item": "https://deportivospro.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Pronósticos Colombia",
                "item": "https://deportivospro.com/pronosticos-colombia"
              }
            ]
          })}
        </script>

        {/* STRUCTURED DATA - Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Pronósticos Deportivos",
            "provider": {
              "@type": "Organization",
              "name": "DeportivosPro",
              "url": "https://deportivospro.com"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Colombia"
            },
            "description": "Servicio líder de pronósticos deportivos en Colombia con 95% de efectividad comprobada. Análisis con IA avanzada para BetPlay, CodeRed, Rushbet y más.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "COP"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-900 to-gray-900 text-white">
        
        {/* HERO SECTION - OPTIMIZADO PARA SEO */}
        <section className="relative overflow-hidden py-20 px-4">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-green-600/20"></div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-7xl mx-auto relative z-10"
          >
            {/* H1 - MUY IMPORTANTE PARA SEO */}
            <h1 className="text-5xl md:text-7xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-blue-500">
              🏆 Pronósticos Deportivos #1 en Colombia 2025
            </h1>
            
            <p className="text-xl md:text-2xl text-center mb-8 text-gray-300">
              <strong>95% de Efectividad COMPROBADA</strong> | +2 Millones de Usuarios GANANDO
            </p>

            {/* CTA PRINCIPAL */}
            <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/prediccion-zone/join')}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-full text-xl font-bold shadow-2xl hover:shadow-green-500/50 transition-all"
              >
                🚀 OBTENER PRONÓSTICOS GRATIS HOY
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/comprar-vip')}
                className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-8 py-4 rounded-full text-xl font-bold shadow-2xl hover:shadow-orange-500/50 transition-all"
              >
                👑 VER PLANES VIP
              </motion.button>
            </div>
          </motion.div>
        </section>

        {/* FEATURED PICKS SECTION */}
        <section className="py-16 px-4 bg-gray-800/50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">
              🎯 Pronósticos Destacados HOY
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredPicks.map((pick, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-blue-600/30 to-purple-600/30 p-6 rounded-xl border border-blue-500/30 hover:border-green-500/50 transition-all cursor-pointer"
                  onClick={() => navigate('/tips-free')}
                >
                  <div className="text-sm text-blue-300 mb-2">{pick.type}</div>
                  <h3 className="text-xl font-bold mb-2">{pick.title}</h3>
                  <p className="text-gray-300 mb-1">{pick.sport}</p>
                  <p className="text-sm text-gray-400 mb-3">{pick.league}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-green-400 font-bold text-lg">{pick.confidence}</span>
                    <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-xs">Efectividad</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* POR QUÉ DEPORTIVOSPRO - CONTENT SEO */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">
              ⚡ ¿Por Qué DeportivosPro es #1 en Colombia?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-br from-green-600/20 to-blue-600/20 p-8 rounded-xl border border-green-500/30"
              >
                <h3 className="text-2xl font-bold mb-4 text-green-400">🤖 Inteligencia Artificial Avanzada</h3>
                <p className="text-gray-300">
                  Nuestro sistema de <strong>IA de última generación</strong> analiza más de <strong>50,000 variables</strong> en tiempo real para cada partido. Machine Learning, Big Data y análisis predictivo trabajando 24/7 para darte las <strong>mejores predicciones</strong>.
                </p>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 p-8 rounded-xl border border-blue-500/30"
              >
                <h3 className="text-2xl font-bold mb-4 text-blue-400">📊 95% de Efectividad Comprobada</h3>
                <p className="text-gray-300">
                  Mantenemos una <strong>tasa de acierto del 95%</strong> verificable públicamente. Todos nuestros resultados son <strong>transparentes</strong> y puedes consultarlos en nuestro historial. Miles de usuarios ganan con nosotros cada día.
                </p>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 p-8 rounded-xl border border-purple-500/30"
              >
                <h3 className="text-2xl font-bold mb-4 text-purple-400">🏆 Compatible con Todas las Casas de Apuestas</h3>
                <p className="text-gray-300">
                  Nuestros pronósticos funcionan en <strong>BetPlay, CodeRed, Rushbet, Wplay, Betsson</strong> y más de <strong>30 casas de apuestas</strong>. Comparamos cuotas en tiempo real para que siempre obtengas el <strong>máximo valor</strong>.
                </p>
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-br from-orange-600/20 to-red-600/20 p-8 rounded-xl border border-orange-500/30"
              >
                <h3 className="text-2xl font-bold mb-4 text-orange-400">🎯 Análisis Experto + Estadísticas</h3>
                <p className="text-gray-300">
                  Cada pronóstico incluye <strong>análisis detallado</strong>, estadísticas avanzadas (xG, xA), lesiones, alineaciones, forma reciente y mucho más. Equipo de <strong>analistas profesionales certificados</strong> trabajando para ti.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CASAS DE APUESTAS - SEO CONTENT */}
        <section className="py-16 px-4 bg-gray-800/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-8">
              🎰 Compatible con las Mejores Casas de Apuestas en Colombia
            </h2>
            <p className="text-center text-gray-300 mb-12 text-lg">
              Nuestros pronósticos deportivos funcionan en TODAS las principales plataformas de apuestas legales en Colombia
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {casasApuestas.map((casa, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-gradient-to-br from-blue-600/30 to-green-600/30 p-6 rounded-xl text-center font-bold text-xl border border-blue-500/30 cursor-pointer"
                >
                  {casa}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* DEPORTES CUBIERTOS */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">
              ⚽ Todos los Deportes Cubiertos en 2025
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-green-600/20 to-blue-600/20 p-6 rounded-xl border border-green-500/30">
                <h3 className="text-2xl font-bold mb-4">⚽ Fútbol</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>✅ Liga Colombiana BetPlay Dimayor</li>
                  <li>✅ Champions League</li>
                  <li>✅ Premier League</li>
                  <li>✅ LaLiga EA Sports</li>
                  <li>✅ Copa Libertadores</li>
                  <li>✅ Eliminatorias Qatar 2026</li>
                  <li>✅ Y muchas ligas más...</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 p-6 rounded-xl border border-orange-500/30">
                <h3 className="text-2xl font-bold mb-4">🏀 Baloncesto</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>✅ NBA</li>
                  <li>✅ Euroliga</li>
                  <li>✅ FIBA</li>
                  <li>✅ NCAA</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 p-6 rounded-xl border border-blue-500/30">
                <h3 className="text-2xl font-bold mb-4">🎾 Tenis y Más</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>✅ ATP / WTA</li>
                  <li>✅ Grand Slams</li>
                  <li>✅ UFC / MMA</li>
                  <li>✅ eSports</li>
                  <li>✅ Béisbol</li>
                  <li>✅ NFL</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* SEO CONTENT - FAQ STYLE */}
        <section className="py-16 px-4 bg-gray-800/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">
              ❓ Preguntas Frecuentes sobre Pronósticos Deportivos en Colombia
            </h2>
            
            <div className="space-y-6">
              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                <h3 className="text-xl font-bold mb-3 text-green-400">
                  ¿Son confiables los pronósticos deportivos de DeportivosPro?
                </h3>
                <p className="text-gray-300">
                  ¡Absolutamente! Mantenemos una <strong>efectividad del 95% comprobada</strong> públicamente. Usamos <strong>inteligencia artificial de última generación</strong>, análisis de big data y un equipo de expertos profesionales certificados. Todos nuestros resultados son verificables y transparentes.
                </p>
              </div>

              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                <h3 className="text-xl font-bold mb-3 text-blue-400">
                  ¿Cómo puedo empezar a usar los pronósticos gratis?
                </h3>
                <p className="text-gray-300">
                  Es muy simple: solo <strong>regístrate gratis</strong> en nuestra plataforma, toma menos de 30 segundos. Inmediatamente tendrás acceso a <strong>pronósticos gratuitos diarios</strong> de alta calidad. Para desbloquear funciones VIP premium, puedes suscribirte desde solo $9.900 COP/mes.
                </p>
              </div>

              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                <h3 className="text-xl font-bold mb-3 text-purple-400">
                  ¿En qué casas de apuestas funcionan estos pronósticos?
                </h3>
                <p className="text-gray-300">
                  Nuestros pronósticos son compatibles con <strong>TODAS las principales casas de apuestas en Colombia</strong>: BetPlay, CodeRed, Rushbet, Wplay, Betsson, bet365, 1xBet, Rivalo, Betway, William Hill y más de 30 operadores. Comparamos cuotas automáticamente para que obtengas el mejor valor.
                </p>
              </div>

              <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-700">
                <h3 className="text-xl font-bold mb-3 text-orange-400">
                  ¿Qué diferencia a DeportivosPro de otras plataformas?
                </h3>
                <p className="text-gray-300">
                  Somos la <strong>ÚNICA plataforma en Colombia</strong> que combina IA de última generación con análisis experto humano. Procesamos más de 50,000 variables por partido, ofrecemos transparencia total en resultados, soporte 24/7, comparador de cuotas en tiempo real y una comunidad de +2 millones de usuarios ganadores.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA FINAL - CONVERSION */}
        <section className="py-20 px-4 bg-gradient-to-r from-green-600/30 to-blue-600/30">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-5xl font-bold mb-6">
              🚀 ¡Comienza a GANAR HOY con los Mejores Pronósticos de Colombia!
            </h2>
            <p className="text-2xl mb-8 text-gray-300">
              Únete a los +2 MILLONES de usuarios que ya están ganando con DeportivosPro
            </p>
            
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/prediccion-zone/join')}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white px-12 py-5 rounded-full text-2xl font-bold shadow-2xl hover:shadow-green-500/50 transition-all"
              >
                💰 REGISTRARME GRATIS AHORA
              </motion.button>
            </div>

            <p className="mt-8 text-gray-400">
              ✅ Sin tarjeta de crédito | ✅ Registro en 30 segundos | ✅ Pronósticos gratis inmediatos
            </p>
          </motion.div>
        </section>

        {/* FOOTER SEO - KEYWORDS RICH */}
        <footer className="py-12 px-4 bg-gray-900 border-t border-gray-800">
          <div className="max-w-6xl mx-auto">
            <div className="text-center text-gray-400 space-y-4">
              <p className="text-sm">
                <strong>DeportivosPro</strong> - La plataforma #1 de pronósticos deportivos en Colombia 2025
              </p>
              <p className="text-xs">
                Pronósticos deportivos | Predicciones fútbol Colombia | Apuestas deportivas | BetPlay | CodeRed | Rushbet | Wplay | 
                Liga Colombiana | Champions League | Premier League | Inteligencia Artificial | IA Apuestas | 
                Tips deportivos | Picks ganadores | Colombia 2025
              </p>
              <p className="text-xs text-gray-500">
                © 2025 DeportivosPro. Todos los derechos reservados. | 
                Apuesta responsablemente | +18 años
              </p>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
};

export default PronosticosColombiaLanding;

